import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Modal, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { deleteUniformIssueMutation } from "@src/util/mutation/uniform.mutation";
import { IssueRequestDrawer, Lines, RatingTag, SOURCE_LABEL, StatusTag, UniformDecisionModal, heldText, reasonLabel, ukDate } from "./uniformParts";
import { ReceiptStatusTag } from "./uniformRecords";

/**
 * Uniform requests. Roles that can edit Uniform (`permission` = the role's Uniform permission)
 * get Approve / Decline / Issue / Cancel; the employee's name opens their Uniform tab.
 */
export const UniformRequestsTable = ({ requests, loading, permission, showEmployee = true, onChanged, emptyText, pagination = { pageSize: 25, hideOnSinglePage: true } }) => {
    const [decision, setDecision] = useState(null);
    const [issuing, setIssuing] = useState(null);
    const canModify = permission?.access !== false && permission?.modify !== false;

    const columns = [
        showEmployee && { title: "Employee", dataIndex: "employeeName", render: (v, row) => <Link className="cmp-link" to={`/user/employee-detail/${row.employeeId}/tab/uniform`}>{v || "--"}</Link> },
        { title: "Asked for", dataIndex: "lines", render: (lines, row) => (
            <div>
                <Lines lines={lines} />
                {row.status === "issued" && row.issuedSummary && row.issuedSummary !== row.summary && <span className="cmp-muted">Issued: {row.issuedSummary}</span>}
            </div>
        ) },
        { title: "Reason", dataIndex: "reason", render: (v, row) => (
            <div className="cmp-person">
                <span>{reasonLabel(v)}</span>
                {row.note && <span className="cmp-muted">"{row.note}"</span>}
            </div>
        ) },
        { title: "Asked", dataIndex: "createdAt", render: (v, row) => (
            <div className="cmp-person"><span>{ukDate(v)}</span><span className="cmp-muted">{SOURCE_LABEL[row.source] || ""}</span></div>
        ) },
        { title: "Status", dataIndex: "status", render: (v, row) => (
            <div className="cmp-person">
                <span><StatusTag status={v} /></span>
                {v === "issued" ? <span className="cmp-muted">{ukDate(row.issuedDate)}{row.issuedByName ? ` · ${row.issuedByName}` : ""}</span>
                    : row.decidedByName ? <span className="cmp-muted">{row.decidedByName}</span> : null}
                {row.decisionNote && <span className="cmp-muted">"{row.decisionNote}"</span>}
            </div>
        ) },
        canModify && { title: "", dataIndex: "actions", align: "right", render: (_, row) => (
            <div className="uni-actions">
                {canModify && row.status === "pending" && <>
                    <button className="drawer-filled-btn" onClick={() => setDecision({ mode: "approve", request: row })}>Approve</button>
                    <button className="sm-btn" onClick={() => setDecision({ mode: "reject", request: row })}>Decline</button>
                </>}
                {canModify && ["pending", "approved"].includes(row.status) && <button className={row.status === "approved" ? "drawer-filled-btn" : "sm-btn"} onClick={() => setIssuing(row)}>Issue</button>}
                {canModify && row.status === "approved" && <button className="sm-btn" onClick={() => setDecision({ mode: "cancel", request: row })}>Cancel</button>}
            </div>
        ) },
    ].filter(Boolean);

    return (
        <>
            <Table
                className="curvedTable"
                loading={loading}
                columns={columns}
                dataSource={(requests || []).map((row) => ({ ...row, key: row._id }))}
                pagination={pagination}
                locale={{ emptyText: emptyText || "No uniform requests." }}
            />
            <UniformDecisionModal mode={decision?.mode} request={decision?.request} onClose={() => setDecision(null)} onDone={onChanged} />
            <IssueRequestDrawer request={issuing} onClose={() => setIssuing(null)} onDone={onChanged} />
        </>
    );
};

/**
 * The issued log: one row per item line handed over, with what's still held, when it's due for
 * renewal and whether the officer has signed for it (`onOpenReceipt` opens the receipt). Like
 * the other grids, rows are ticked on the left and acted on from the bar above: Return
 * (`onReturn` gets the ticked rows) and Remove, for an entry made by mistake that hasn't been
 * signed for or returned against.
 */
export const UniformIssuesTable = ({ issues, loading, permission, showEmployee = true, onChanged, onOpenReceipt, onReturn, emptyText, pagination = { pageSize: 25, hideOnSinglePage: true } }) => {
    const dispatch = useDispatch();
    const [remove] = useMutation(deleteUniformIssueMutation);
    const [selected, setSelected] = useState([]);
    const canRemove = permission?.access !== false && permission?.archive !== false;
    const canReturn = !!onReturn && permission?.access !== false && permission?.modify !== false;
    const removable = (row) => row.receiptStatus !== "signed" && !row.returns?.length;
    const chosen = (issues || []).filter((row) => selected.includes(row._id));
    useEffect(() => { setSelected((prev) => prev.filter((id) => (issues || []).some((row) => row._id === id && row.held > 0))); }, [issues]);

    const removeRows = (rows) => Modal.confirm({
        title: rows.length === 1 ? `Remove ${rows[0].quantity} x ${rows[0].itemName}${rows[0].size ? ` (${rows[0].size})` : ""} from the log?` : `Remove ${rows.length} entries from the log?`,
        content: "Only for entries made by mistake - once uniform is signed for, record a return instead.",
        okText: "Remove",
        okButtonProps: { danger: true },
        cancelText: "Keep",
        onOk: async () => {
            try {
                for (const row of rows) await remove({ variables: { id: row._id } });
                dispatch(setNotification({ error: false, notificationState: true, message: rows.length === 1 ? "Removed from the issued log" : `${rows.length} entries removed` }));
                setSelected([]);
                onChanged?.();
            } catch (err) {
                dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
            }
        },
    });

    // the bar above the grid with rows ticked, like Settings > Skills
    const selectionBar = () => (
        <div className='table-footer' style={{ backgroundColor: 'rgb(245, 248, 250)' }} id="selection-options">
            <small className='small-text'>{chosen.length} selected</small>
            {canReturn && <div onClick={() => onReturn(chosen)}><FontAwesomeIcon icon={faRotateLeft} style={{ marginRight: 5 }} /><span>Return</span></div>}
            {canRemove && chosen.every(removable) && <div onClick={() => removeRows(chosen)}><FontAwesomeIcon icon={faTrashCan} style={{ marginRight: 5 }} /><span>Remove</span></div>}
        </div>
    );

    const columns = [
        { title: "Date", dataIndex: "issuedDate", render: (v) => ukDate(v), width: 120 },
        showEmployee && { title: "Employee", dataIndex: "employeeName", render: (v, row) => <Link className="cmp-link" to={`/user/employee-detail/${row.employeeId}/tab/uniform`}>{v || "--"}</Link> },
        { title: "Item", dataIndex: "itemName", render: (v, row) => (
            <div className="cmp-person">
                <span>{v} <RatingTag rating={row.rating} /></span>
                {row.note && <span className="cmp-muted">"{row.note}"</span>}
            </div>
        ) },
        { title: "Size", dataIndex: "size", render: (v) => v || <span className="cmp-muted">One size</span> },
        { title: "Qty", dataIndex: "quantity", width: 60 },
        { title: "Held", dataIndex: "held", render: (_, row) => (
            <div className="cmp-person">
                <span>{heldText(row)}</span>
                <span className="cmp-muted">{row.returnable === false ? "Personal issue" : "Company property"}</span>
            </div>
        ) },
        { title: "Renewal due", dataIndex: "renewDue", render: (v) => (!v ? <span className="cmp-muted">--</span>
            : v < dayjs().format("YYYY-MM-DD") ? <Tag color="red">Overdue {ukDate(v)}</Tag>
            : v <= dayjs().add(30, "day").format("YYYY-MM-DD") ? <Tag color="gold">{ukDate(v)}</Tag> : ukDate(v)) },
        { title: "Receipt", dataIndex: "receiptStatus", render: (v, row) => (row.receiptId
            ? <button className="uni-link-btn" onClick={() => onOpenReceipt?.(row.receiptId)}><ReceiptStatusTag status={v} /></button>
            : <span className="cmp-muted">Before receipts</span>) },
        { title: "Issued by", dataIndex: "issuedByName", render: (v, row) => (
            <div className="cmp-person"><span>{v || "--"}</span><span className="cmp-muted">{row.requestId ? "From a request" : "Logged directly"}</span></div>
        ) },
    ].filter(Boolean);

    return (
        <Table
            className="moveGroupTable curvedTable"
            loading={loading}
            columns={columns}
            // only what's still held can be returned or removed
            rowSelection={canReturn || canRemove ? { selectedRowKeys: selected, onChange: setSelected, getCheckboxProps: (row) => ({ disabled: !(row.held > 0) }) } : undefined}
            title={chosen.length ? selectionBar : null}
            dataSource={(issues || []).map((row) => ({ ...row, key: row._id }))}
            pagination={pagination}
            locale={{ emptyText: emptyText || "No uniform issued yet." }}
        />
    );
};
